import { Money } from './money';

import { convertDateFromDateArray } from '@/utils/time';
import {
    BackendType,
    convertIsoDateStringToDate,
    FrontendType,
    MultiLangString,
    ObjectType,
    StarjackDate,
    StarjackDateISO,
} from './general';
import { ProductType } from '@/models/product';
import { Address } from '@/models/address';
import { ChipProps } from '@mui/material';
import { Environment } from './environment';

export enum OrderStatus {
    NEW = 'NEW',
    ORDERED = 'ORDERED',
    PAID = 'PAID',
    REFUNDED = 'REFUNDED',
    CANCELED = 'CANCELED',
    PRODUCED = 'PRODUCED',
}

export const getOrderStatusAsString = (status: OrderStatus) => {
    switch (status) {
        case 'ORDERED':
            return 'Bestellt';
        case 'PRODUCED':
            return 'Produziert';
        case 'PAID':
            return 'Bezahlt';
        default:
            return status;
    }
};

export const getTicketStatusColor = (
    status: Ticket['status']
): ChipProps['color'] => {
    switch (status) {
        case 'PAID':
        case 'PRODUCED':
            return 'success';
        case 'CANCELED':
            return 'error';
        case 'REFUNDED':
            return 'warning';
        default:
            return 'primary';
    }
};

export type Skier = {
    barcodeNumber: string;
    downloadLink: string;
    firstname: string;
    lastname: string;
    permissionNumber: string;
    serialNumber: string;
};

export type Identification<T extends ObjectType = FrontendType> = {
    amount: Money;
    birthday: StarjackDate<T>;
    creationDate: StarjackDate<T>;
    deletionDate: StarjackDate<T>;
    depositAmount: Money;
    firstname: string;
    lastname: string;
    gender: 'MALE' | 'FEMALE';
    id: string;
    permissionNumber: string;
    barcodeNumber: string;
    downloadLink: string;
    type: string;
    keycard: {
        cardType: string;
        external: false;
        producer: string;
        serialNumber: string;
    };
    orderDate: StarjackDate<T>;
    orderInternalDate: StarjackDate<T>;
    productionDate: StarjackDate<T>;
    shippingAmount: Money;
    status: OrderStatus;
    keycardOrderedSameTime: boolean;
    title: string;
};

const convertIdentificationToFrontend = (
    identification: Identification<BackendType>
): Identification => ({
    ...identification,
    birthday: convertDateFromDateArray(identification.birthday),
    creationDate: convertDateFromDateArray(identification.creationDate),
    deletionDate: convertDateFromDateArray(identification.deletionDate),
    orderDate: convertDateFromDateArray(identification.orderDate),
    orderInternalDate: convertDateFromDateArray(
        identification.orderInternalDate
    ),
    productionDate: convertDateFromDateArray(identification.productionDate),
});

export type Ticket<T extends ObjectType = FrontendType> = {
    barcodeNumber: string;
    cancelable: boolean;
    cancelableCondition: boolean;
    cancellationDate: StarjackDate<T>;
    cancelledBy: string;
    confirmationNumber: string;
    deliveryAddress: {
        firstname: string;
        lastname: string;
    } & Address;
    duration: number;
    email: string;
    firstname: string;
    id: string;
    identificationType: 'KEYCARD' | 'PICKUP' | 'BARCODE' | 'E_TICKET';
    identifications: Identification<T>[];
    integrationRef: string;
    lastname: string;
    name: string;
    orderDate: StarjackDate<T>;
    orderId: string;
    orderDisplayId: string;
    permissionNumber: string;
    personName: string;
    price: Money;
    priceReason: string;
    productionDate: StarjackDate<T>;
    skiers: Skier[];
    startDate: StarjackDate<T>;
    status: OrderStatus;
    type: ProductType;
    userId: string;
    liftOperatorId: string;
    pdfUrl: string;
    orderReference: string;
    environment: Environment;
    insurance: {
        bookedAt: StarjackDateISO<T>;
        canceledAt: StarjackDateISO<T>;
        createdAt: StarjackDateISO<T>;
        policyNumber: string;
        price: Money;
        revokedAt: StarjackDateISO<T>;
        tariffName: MultiLangString;
        status: InsuranceStatus;
        revocable: boolean;
        cancelable: boolean;
    } | null;
};

export enum InsuranceStatus {
    CREATED = 'CREATED',
    PAID = 'PAID',
    REFUNDED = 'REFUNDED',
    CANCELED = 'CANCELED',
    BOOKED = 'BOOKED',
    REVOKED = 'REVOKED',
    PERMISSION_BLOCKED = 'PERMISSION_BLOCKED',
}

export const convertTicketToFrontend = (
    responseObject: Ticket<BackendType>
): Ticket => ({
    ...responseObject,
    startDate: convertDateFromDateArray(responseObject.startDate),
    orderDate: convertDateFromDateArray(responseObject.orderDate),
    productionDate: convertDateFromDateArray(responseObject.productionDate),
    cancellationDate: convertDateFromDateArray(responseObject.cancellationDate),
    identifications:
        responseObject.identifications?.map(convertIdentificationToFrontend) ||
        [],
    insurance: responseObject.insurance
        ? {
              ...responseObject.insurance,
              createdAt: convertIsoDateStringToDate(
                  responseObject.insurance?.createdAt
              ),
              bookedAt: convertIsoDateStringToDate(
                  responseObject.insurance?.bookedAt
              ),
              canceledAt: convertIsoDateStringToDate(
                  responseObject.insurance?.canceledAt
              ),
              revokedAt: convertIsoDateStringToDate(
                  responseObject.insurance?.revokedAt
              ),
          }
        : null,
});

export type OrderTicket<T extends ObjectType = FrontendType> = {
    barcodeNumber: string;
    cancelable: boolean;
    cancelableCondition: boolean;
    cancellationDate: StarjackDate<T>;
    cancelledBy: string;
    confirmationNumber: string;
    deliveryAddress: {
        firstname: string;
        lastname: string;
    } & Address;
    duration: number;
    email: string;
    firstname: string;
    id: string;
    identificationType: 'KEYCARD' | 'PICKUP' | 'BARCODE' | 'E_TICKET';
    identifications: Identification<T>[];
    integrationRef: string;
    lastname: string;
    name: MultiLangString;
    orderDate: StarjackDate<T>;
    orderId: string;
    orderDisplayId: string;
    permissionNumber: string;
    personName: MultiLangString;
    price: Money;
    priceReason: string;
    productionDate: StarjackDate<T>;
    skiers: Skier[];
    startDate: StarjackDate<T>;
    status: OrderStatus;
    type: ProductType;
    userId: string;
    liftOperator: MultiLangString;
    pdfUrl: string;
};

export const convertOrderTicketToFrontend = (
    responseObject: OrderTicket<BackendType>
): OrderTicket => ({
    ...responseObject,
    startDate: convertDateFromDateArray(responseObject.startDate),
    orderDate: convertDateFromDateArray(responseObject.orderDate),
    productionDate: convertDateFromDateArray(responseObject.productionDate),
    cancellationDate: convertDateFromDateArray(responseObject.cancellationDate),
    identifications:
        responseObject.identifications?.map(convertIdentificationToFrontend) ||
        [],
});

export const getDurationString = (duration: number, type: ProductType) => {
    if (type === ProductType.SEASON) {
        return '1 Saison';
    }

    return `${duration} 
        ${duration === 1 ? 'Tag' : 'Tage'}
    `;
};
